/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { KillswitchDao } from './killswitch.dao';
import { Observable } from 'rxjs';
import { Chain, KillSwitches } from '@shared/data-access/interfaces';


@Injectable({
	providedIn: 'root'
})
export class KillswitchService {
	constructor(
		private readonly killswitchDao: KillswitchDao,
		private readonly chain: Chain,
		private readonly activatedRoute: ActivatedRoute,
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {}

	/**
	 * @description
	 * Gets all of the environment key value pairs from the kill switch DAO
	 */
	getKillSwitchesEnvironment$(): Observable<KillSwitches> {
		return this.killswitchDao.getKillSwitchesFromEnvironment$();
	}

	/**
	 * @returns an Observable Record with a boolean value for each killswitch name passed in via string array
	 * @example ```{availabilityOnProductCardsEnabled: true, favoritesEnabled: true, filterMobileButtonEnabled: false}```
	*/
	getKillSwitchData$(killswitchNames: string[]): Observable<Record<string, boolean>> {
		const win = this.document?.defaultView;
		const chain = this.chain.chainIdentifierAbbr;

		return this.activatedRoute.queryParamMap.pipe(
			withLatestFrom(this.getKillSwitchesEnvironment$()),
			map(([params, mainKillSwitchObject]: [ParamMap, KillSwitches]) => {
				const permissionSet: Record<string, boolean> = {};
				const flagParams: string[] = params.keys
						.filter((key) => key.startsWith('flag_'))
						.map((key) => key.substring(5)); // remove the 'flag_' prefix

				for (const ks of killswitchNames) {
					const overrideName = `${ks}Override`;
					const override = mainKillSwitchObject[overrideName] !== undefined && mainKillSwitchObject[overrideName] !== null ?
						mainKillSwitchObject[overrideName][chain] : undefined;
					// Override Priority 1. URL Query Param flag_<ks> takes precedence over all other killswitches
					if (flagParams.includes(ks)) {
						permissionSet[ks] = params.get(`flag_${ks}`) === 'true';
						continue;
					}
					// Override Priority 2. If the killswitch is available in the window object, use the window object value
					if (win[ks] !== undefined && isPlatformBrowser(this.platformId)) {
						permissionSet[ks] = !!win[ks];
						continue;
					}
					// Override Priority 3. If a killswitch with 'Override' appended to the name is present, use the 'Override' value
					if (override !== undefined) {
						permissionSet[ks] = override;
						continue;
					}
					// Default. If no other overrides are present, use the main killswitch object value
					if (mainKillSwitchObject[ks]) {
						permissionSet[ks] = mainKillSwitchObject[ks][chain];
					}
				}

				return permissionSet;
			})
		);
	}

	/**
	 * @returns an Observable Record with a boolean value for each killswitch for the current chain
	 * @example ```{availabilityOnProductCardsEnabled: true, favoritesEnabled: true, filterMobileButtonEnabled: false}```
	*/
	getAllKillSwitchesForCurrentChain$(): Observable<Record<string, boolean>> {
		const chain = this.chain.chainIdentifierAbbr;
		return this.getKillSwitchesEnvironment$().pipe(
			map((killSwitches) => {
				const chainKillSwitches: Record<string, boolean> = {};
				for (const [key, value] of Object.entries(killSwitches)) {
					if (value[chain] !== undefined) {
						chainKillSwitches[key] = value[chain];
					}
				}
				return Object.keys(chainKillSwitches);
			}),
			switchMap((killSwitchNames) => this.getKillSwitchData$(killSwitchNames))
		);
	}
}
